/**
 * Coordinate-system helpers for the measurement detail page.
 *
 * TrackMan football coordinates (the system used end-to-end in BFF + 3D chart):
 *   X — depth from defending goal line toward midfield (m, always ≥ 0 for kicks)
 *   Y — vertical height (m, always ≥ 0)
 *   Z — lateral, sign convention defined by the football SDK
 *   origin = centre of the goal line
 *
 * The 3D chart (`BallTrajectory3DChart`) renders this directly: its `toScene`
 * mapping puts soccer +Z on the viewer's right when looking from the kicker
 * toward the goal. That orientation has been verified against the SDK / live
 * data and is treated as the source of truth for "which side is +Z".
 *
 * The 2D charts (`ShotLocationChart` top-down, `GoalFrontViewChart` front-on)
 * were originally authored against the legacy player-report data flow which
 * fed in Z with the OPPOSITE sign convention. On the measurement-detail page
 * we feed the SAME raw trajectory into all three charts, so without a flip
 * the 3D view shows the kick on one side of the goal and the 2D views show
 * it on the other side — which was confusing.
 *
 * Rather than touching the 2D charts (used elsewhere) we negate Z at the
 * adapter boundary here. Centralising it as a helper means there is exactly
 * one place to revisit if the SDK/snapshot conventions ever change.
 */

/**
 * Convert a soccer-coordinate lateral (Z) value into the lateral value
 * expected by the 2D top-down `ShotLocationChart` and the 2D front-on
 * `GoalFrontViewChart`.
 */
export function soccerZToChartZ(z: number): number {
  return -z;
}

/** Convenience wrapper for goal-crossing pairs used by both 2D charts. */
export function soccerGoalCrossingToChart(g: { y: number; z: number }): { y: number; z: number } {
  return { y: g.y, z: soccerZToChartZ(g.z) };
}

/** Convenience wrapper for a kick-location point used by the top-down chart. */
export function soccerKickLocationToChart(k: { x: number; y: number; z: number }): { x: number; y: number; z: number } {
  return { x: k.x, y: k.y, z: soccerZToChartZ(k.z) };
}
